/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { FloatingPortal } from '@floating-ui/react';
import classNames from 'classnames';
import { Resizable, type ResizableProps } from 're-resizable';
import type React from 'react';
import { type ComponentProps, useCallback } from 'react';

import {
  type HtmlAttributes,
  type PolymorphicCommonProps,
} from '../_common/types';
import { CleanIconButton } from '../clean-icon-button';
import { ConditionalWrap } from '../conditional-wrap';
import { XMarkIconOutline } from '../icons';
import { Typography } from '../typography';

type DrawerPosition = 'left' | 'right';

type DrawerType = 'overlay' | 'push';

type DrawerProps = {
  /** Controls whether the drawer is open (expanded) or closed (collapsed). */
  isExpanded: boolean;

  /** Callback functions fired when the drawer's expanded state changes. */
  onExpandedChange?: (expanded: boolean) => void;

  /** Position where the drawer appears on screen. By default, it appears from the left. */
  position?: DrawerPosition;

  /** How the drawer affects the layout of sibling content. By default, it overlays content. */
  type?: DrawerType;

  /** Enables horizontal resizing of the drawer, by default it is not resizable. */
  isResizeable?: boolean;

  /** To use this prop the isResizeable prop must be set to true. For overriding the third party library props: https://github.com/bokuweb/re-resizable */
  resizeableProps?: ResizableProps;

  /** Whether a close button (X) is displayed in the drawer. */
  isCloseable?: boolean;

  /** Whether to portal the content */
  isPortaled?: boolean;

  /** The content to display within the drawer. */
  children: React.ReactNode;

  /** Additional CSS classes to apply to the drawer container. */
  className?: string;

  /**
   * Props to pass to the portal from @floating-ui/react FloatingPortal
   * @see https://floating-ui.com/docs/floatingportal
   */
  portalProps?: ComponentProps<typeof FloatingPortal>;
};

const DrawerComponent = function DrawerComponent<
  T extends React.ElementType = 'div',
>({
  children,
  className = '',
  isExpanded,
  onExpandedChange,
  position = 'left',
  type = 'overlay',
  isResizeable = false,
  resizeableProps,
  isCloseable = true,
  isPortaled = false,
  portalProps = {},
  htmlAttributes,
  style,
  ref,
  as,
  ...restProps
}: PolymorphicCommonProps<T, DrawerProps>) {
  const setOnExpandedChange = useCallback(() => {
    if (onExpandedChange) {
      onExpandedChange(!isExpanded);
    }
  }, [isExpanded, onExpandedChange]);

  const classes = classNames('ndl-drawer', className, {
    'ndl-drawer-expanded': isExpanded,
    'ndl-drawer-left': position === 'left',
    'ndl-drawer-overlay': type === 'overlay',
    'ndl-drawer-push': type === 'push',
    'ndl-drawer-right': position === 'right',
  });

  const resizableStylePosition = type === 'overlay' ? 'absolute' : 'relative';
  const shouldRenderInPortal = isPortaled && type === 'overlay';
  const Component = as ?? 'div';

  const Close = () => (
    <>
      {isCloseable && (
        <CleanIconButton
          className="ndl-drawer-close-button"
          onClick={setOnExpandedChange}
          description="Close"
          size="medium"
          htmlAttributes={{
            title: 'Close drawer',
          }}
        >
          <XMarkIconOutline />
        </CleanIconButton>
      )}
    </>
  );

  if (isResizeable) {
    return (
      <ConditionalWrap
        shouldWrap={shouldRenderInPortal}
        wrap={(wrapChildren) => (
          <FloatingPortal preserveTabOrder={true} {...portalProps}>
            {wrapChildren}
          </FloatingPortal>
        )}
      >
        <Resizable
          as={Component}
          defaultSize={{
            height: '100%',
            width: 'auto',
          }}
          {...resizeableProps}
          className={classes}
          style={{
            position: resizableStylePosition,
            ...style,
            ...resizeableProps?.style,
          }}
          boundsByDirection={true}
          bounds="parent"
          handleStyles={{
            /*
             * adding a small offset to the handle to make it easier to click
             * if the content of the drawer is scrollable, most noticeable if not using
             * a mouse on mac or chrome on windows with the fluent scrollbars
             */
            right: {
              right: '-8px',
            },
          }}
          enable={{
            bottom: false,
            bottomLeft: false,
            bottomRight: false,
            left: position === 'right',
            right: position === 'left',
            top: false,
            topLeft: false,
            topRight: false,
          }}
          ref={ref}
          {...restProps}
          {...htmlAttributes}
        >
          {children}
          <Close />
        </Resizable>
      </ConditionalWrap>
    );
  }

  return (
    <ConditionalWrap
      shouldWrap={shouldRenderInPortal}
      wrap={(wrapChildren) => (
        <FloatingPortal preserveTabOrder={true} {...portalProps}>
          {wrapChildren}
        </FloatingPortal>
      )}
    >
      <Component
        className={classes}
        style={style}
        ref={ref}
        {...restProps}
        {...htmlAttributes}
      >
        {children}
        <Close />
      </Component>
    </ConditionalWrap>
  );
};

DrawerComponent.displayName = 'Drawer';

type DrawerHeaderProps = {
  children: React.ReactNode;
  className?: string;
  htmlAttributes?: HtmlAttributes<'h5'>;
};

const DrawerHeader = ({
  children,
  className = '',
  htmlAttributes,
  ...restProps
}: DrawerHeaderProps) => {
  const classes = classNames('ndl-drawer-header', className);

  if (typeof children === 'string' || typeof children === 'number') {
    return (
      <Typography
        className={classes}
        variant="title-3"
        {...restProps}
        {...htmlAttributes}
      >
        {children}
      </Typography>
    );
  }

  return (
    <div className={classes} {...restProps} {...htmlAttributes}>
      {children}
    </div>
  );
};

type DrawerActionsProps = {
  children: React.ReactNode;
  className?: string;
  htmlAttributes?: HtmlAttributes<'div'>;
};

const DrawerActions = ({
  children,
  className = '',
  htmlAttributes,
  ...restProps
}: DrawerActionsProps) => {
  const classes = classNames('ndl-drawer-actions', className);

  return (
    <div className={classes} {...restProps} {...htmlAttributes}>
      {children}
    </div>
  );
};

type DrawerBodyProps = {
  children: React.ReactNode;
  className?: string;
  htmlAttributes?: HtmlAttributes<'div'>;
};

const DrawerBody = ({
  children,
  className = '',
  htmlAttributes,
  ...restProps
}: DrawerBodyProps) => {
  const classes = classNames('ndl-drawer-body', className);

  return (
    <div className="ndl-drawer-body-wrapper">
      <div className={classes} {...restProps} {...htmlAttributes}>
        {children}
      </div>
    </div>
  );
};

const Drawer = Object.assign(DrawerComponent, {
  Actions: DrawerActions,
  Body: DrawerBody,
  Header: DrawerHeader,
});

export { Drawer };
